import React, { useMemo } from 'react';
import { FiExternalLink, FiPause, FiPlay, FiRefreshCw, FiZap, FiWifiOff } from 'react-icons/fi';
import Badge from './Badge';
import { fmtDateTime, fmtDuration, relativeTime } from './format';

const TASK_LABELS = {
  wait_for_reservations_sync: 'Wait for PMS sync',
  run_daily_sms_pipeline: 'Export → clean → dedupe → send',
};

function TaskStep({ task, active }) {
  const label = TASK_LABELS[task.taskId] || task.taskId;
  const state = task.state || 'none';
  const durationMs = task.durationSec != null ? task.durationSec * 1000 : null;
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-[12px] font-medium text-zinc-800">{label}</p>
        <p className="truncate font-mono text-[10px] text-zinc-400">{task.taskId}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2 text-[10.5px] text-zinc-500">
        {durationMs != null ? <span className="tabular-nums">{fmtDuration(durationMs)}</span> : null}
        {task.tryNumber > 1 ? <span>try {task.tryNumber}</span> : null}
        <Badge state={state} pulse={active && state === 'running'}>
          {state.replace(/_/g, ' ')}
        </Badge>
      </div>
    </li>
  );
}

function RunChip({ run }) {
  const conf = run.conf || {};
  const live = String(conf.LIVE).toLowerCase() === 'true';
  return (
    <div className="flex items-center gap-2">
      <Badge state={run.state} pulse={run.state === 'running'}>
        {run.state}
      </Badge>
      <Badge tone={live ? 'rose' : 'sky'}>{live ? 'LIVE' : 'dry run'}</Badge>
      <Badge tone="zinc">{run.externalTrigger ? 'manual' : 'scheduled'}</Badge>
    </div>
  );
}

/**
 * Live view of the Airflow DAG: paused state, next run, latest run with task
 * steps, and operator controls (Run now / Pause) for full admins.
 */
export default function PipelineStatusCard({
  status,
  loading,
  refreshing,
  canOperate,
  onRefresh,
  onRequest, // (kind) => void   kind: 'dry_run' | 'live' | 'pause' | 'resume'
}) {
  const airflow = status?.airflow;
  const available = Boolean(airflow?.available);
  const latest = airflow?.latestRun || null;
  const active = airflow?.activeRun || null;

  const nextRunLabel = useMemo(() => {
    if (!airflow?.nextDagRunCreateAfter) return null;
    return `${fmtDateTime(airflow.nextDagRunCreateAfter)} PT · ${relativeTime(airflow.nextDagRunCreateAfter)}`;
  }, [airflow]);

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${available ? (airflow.isPaused ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600') : 'bg-zinc-100 text-zinc-400'}`}>
            {available ? <FiZap className="h-4 w-4" /> : <FiWifiOff className="h-4 w-4" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-zinc-900">Daily pipeline</h2>
              {available ? (
                airflow.isPaused ? (
                  <Badge tone="amber">schedule paused</Badge>
                ) : active ? (
                  <Badge tone="indigo" pulse>
                    {active.state === 'queued' ? 'run queued' : 'running now'}
                  </Badge>
                ) : (
                  <Badge tone="emerald">on schedule</Badge>
                )
              ) : loading ? (
                <Badge tone="zinc">connecting…</Badge>
              ) : (
                <Badge tone="zinc">Airflow unavailable</Badge>
              )}
            </div>
            <p className="text-[11px] text-zinc-500">
              {available
                ? `${airflow.dagId} · ${airflow.timetableDescription || airflow.scheduleInterval || 'daily'} · 12:00 PM Pacific`
                : 'Live status, next run and controls appear once the Airflow API is reachable.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {available && airflow.uiUrl ? (
            <a
              href={airflow.uiUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Open in Airflow <FiExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : null}
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
            aria-label="Refresh pipeline status"
          >
            <FiRefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {canOperate && available ? (
            <>
              <button
                type="button"
                onClick={() => onRequest(airflow.isPaused ? 'resume' : 'pause')}
                className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold ${
                  airflow.isPaused
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                }`}
              >
                {airflow.isPaused ? <FiPlay className="h-3.5 w-3.5" /> : <FiPause className="h-3.5 w-3.5" />}
                {airflow.isPaused ? 'Resume schedule' : 'Pause schedule'}
              </button>
              <button
                type="button"
                disabled={Boolean(active)}
                onClick={() => onRequest('dry_run')}
                title={active ? 'A run is already queued or running' : 'Simulate today’s batch without sending'}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-semibold text-zinc-800 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiPlay className="h-3.5 w-3.5" /> Run dry
              </button>
              <button
                type="button"
                disabled={Boolean(active)}
                onClick={() => onRequest('live')}
                title={active ? 'A run is already queued or running' : 'Send real SMS now'}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-zinc-900 px-2.5 text-xs font-semibold text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiZap className="h-3.5 w-3.5" /> Run LIVE
              </button>
            </>
          ) : null}
        </div>
      </header>

      {!available ? (
        <div className="px-5 py-4 text-xs text-zinc-500">
          {loading ? (
            <div className="h-4 w-56 animate-pulse rounded bg-zinc-100" />
          ) : (
            <>
              Reason: <span className="font-mono">{airflow?.reason || 'unknown'}</span>
              {airflow?.detail ? ` — ${airflow.detail}` : ''}. Analytics below still reflect the database.
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4 px-5 py-4 laptop:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-[12px]">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Next scheduled run</dt>
              <dd className="mt-0.5 font-medium text-zinc-900">
                {airflow.isPaused ? <span className="text-amber-700">Paused — will not fire</span> : nextRunLabel || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Latest run</dt>
              <dd className="mt-0.5 font-medium text-zinc-900">
                {latest ? (
                  <>
                    {fmtDateTime(latest.startDate || latest.queuedAt || latest.logicalDate)} PT
                    <span className="text-zinc-400"> · {relativeTime(latest.startDate || latest.queuedAt || latest.logicalDate)}</span>
                  </>
                ) : (
                  'No runs yet'
                )}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Latest run state</dt>
              <dd className="mt-1">{latest ? <RunChip run={latest} /> : '—'}</dd>
            </div>
            {latest ? (
              <div className="col-span-2">
                <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Run id</dt>
                <dd className="mt-0.5 truncate font-mono text-[11px] text-zinc-600" title={latest.runId}>
                  {latest.runId}
                </dd>
              </div>
            ) : null}
            {latest?.conf?.triggeredBy ? (
              <div className="col-span-2">
                <dt className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Triggered by</dt>
                <dd className="mt-0.5 text-zinc-700">{String(latest.conf.triggeredBy).replace(/^admin:/, '')}</dd>
              </div>
            ) : null}
          </dl>

          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Steps (latest run)</p>
            {airflow.latestTasks?.length ? (
              <ol className="space-y-1.5">
                {airflow.latestTasks.map((t) => (
                  <TaskStep key={t.taskId} task={t} active={Boolean(active)} />
                ))}
              </ol>
            ) : (
              <p className="text-xs text-zinc-400">No task instances yet.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
